import React, { useEffect, useMemo, useState } from 'react';
import { Avatar, SkeletonLoading } from '@saleshandy/design-system';
import { User } from '@saleshandy/icons';

type AvatarCacheEntry =
  | { status: 'loading'; promise: Promise<string> }
  | { status: 'loaded'; objectUrl: string }
  | { status: 'error' };

const avatarLoadCache = new Map<string, AvatarCacheEntry>();

const fetchAvatarObjectUrl = (url: string): Promise<string> => {
  const cachedEntry = avatarLoadCache.get(url);

  if (cachedEntry) {
    if (cachedEntry.status === 'loaded') {
      return Promise.resolve(cachedEntry.objectUrl);
    }
    if (cachedEntry.status === 'loading') {
      return cachedEntry.promise;
    }
  }

  const promise = fetch(url)
    .then(response => {
      if (!response.ok) {
        throw new Error(`Failed to load avatar for ${url}`);
      }
      return response.blob();
    })
    .then(blob => {
      const objectUrl = URL.createObjectURL(blob);
      avatarLoadCache.set(url, { status: 'loaded', objectUrl });
      return objectUrl;
    })
    .catch(error => {
      avatarLoadCache.set(url, { status: 'error' });
      throw error;
    });

  avatarLoadCache.set(url, { status: 'loading', promise });
  return promise;
};

const INVALID_PICTURE_URL =
  'https://static.licdn.com/aero-v1/sc/h/9c8pery4andzj6ohjkjp54ma2';

type ProspectAvatarProps = {
  firstName?: string;
  lastName?: string;
  profilePicture?: string | null;
  size?: number;
};

const DEFAULT_AVATAR_SIZE = 48;

const ProspectAvatar: React.FC<ProspectAvatarProps> = ({
  firstName = '',
  lastName = '',
  profilePicture,
  size = DEFAULT_AVATAR_SIZE,
}) => {
  const sanitizedPictureUrl = useMemo(() => {
    if (!profilePicture || profilePicture === INVALID_PICTURE_URL) {
      return null;
    }
    return profilePicture;
  }, [profilePicture]);

  const cachedEntry = sanitizedPictureUrl
    ? avatarLoadCache.get(sanitizedPictureUrl)
    : undefined;

  const [resolvedSrc, setResolvedSrc] = useState<string | null>(() =>
    cachedEntry?.status === 'loaded' ? cachedEntry.objectUrl : null,
  );
  const [imageError, setImageError] = useState(
    () => cachedEntry?.status === 'error',
  );
  const [imageLoading, setImageLoading] = useState(
    () =>
      Boolean(sanitizedPictureUrl) &&
      (!cachedEntry || cachedEntry.status === 'loading'),
  );

  useEffect(() => {
    let isActive = true;

    if (!sanitizedPictureUrl) {
      setResolvedSrc(null);
      setImageLoading(false);
      setImageError(false);
      return () => {
        isActive = false;
      };
    }

    const cacheEntry = avatarLoadCache.get(sanitizedPictureUrl);
    if (cacheEntry?.status === 'loaded') {
      setResolvedSrc(cacheEntry.objectUrl);
      setImageLoading(false);
      setImageError(false);
      return () => {
        isActive = false;
      };
    }

    if (cacheEntry?.status === 'error') {
      setResolvedSrc(null);
      setImageLoading(false);
      setImageError(true);
      return () => {
        isActive = false;
      };
    }

    setResolvedSrc(null);
    setImageLoading(true);
    setImageError(false);

    const loadPromise =
      cacheEntry?.status === 'loading'
        ? cacheEntry.promise
        : fetchAvatarObjectUrl(sanitizedPictureUrl);

    loadPromise
      .then(objectUrl => {
        if (!isActive) {
          return;
        }
        setResolvedSrc(objectUrl);
        setImageLoading(false);
        setImageError(false);
      })
      .catch(() => {
        if (!isActive) {
          return;
        }
        setResolvedSrc(null);
        setImageError(true);
        setImageLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [sanitizedPictureUrl]);

  const shouldShowFallbackAvatar = !sanitizedPictureUrl || imageError;
  const shouldShowSkeleton =
    Boolean(sanitizedPictureUrl) && imageLoading && !imageError;
  const hasInitials = Boolean(firstName || lastName);
  const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();

  if (shouldShowFallbackAvatar) {
    if (hasInitials) {
      return (
        <Avatar
          firstName={firstName}
          lastName={lastName}
          size="lg"
          className="cs-avatar bs-mr-12"
        />
      );
    }

    return (
      <div className="empty-avatar bs-mr-12">
        <User width={25} height={20} className="blue-txt-11" />
      </div>
    );
  }

  return (
    <div
      className="cs-avatar bs-mr-12 d-flex align-items-center justify-content-center"
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        overflow: 'hidden',
      }}
    >
      {shouldShowSkeleton && (
        <SkeletonLoading className="d-flex" width={size} height={size} circle />
      )}
      {resolvedSrc && (
        <img
          src={resolvedSrc}
          alt={fullName || 'Prospect avatar'}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: shouldShowSkeleton ? 'none' : 'block',
          }}
        />
      )}
    </div>
  );
};

export default ProspectAvatar;

